import { useParams, Link } from 'react-router-dom';
import { projectsData } from '../data/projectsData';
import NotFound from './NotFound';
import LyrikaStanzaPage from './LyrikaStanzaPage';
import SEO from '../components/SEO';
import { getSoftwareAppSchema, getBreadcrumbSchema } from '../data/schemas';

const DiscordBotDetail = () => {
  const { botId } = useParams();
  
  if (botId === 'lyrika-stanza' || botId === 'lyrika' || botId === 'bot-0') {
    return <LyrikaStanzaPage />;
  }

  const discordProject = projectsData.find(p => p.id === 'discordbots');
  
  if (!discordProject || !discordProject.bots) {
    return <NotFound />;
  }

  // Find bot matching slug, id, or lowercased name / name
  const bot = discordProject.bots.find(b => 
    b.slug === botId || 
    b.id === botId || 
    b.name.toLowerCase().replace(/\s+/g, '-') === botId.toLowerCase() ||
    b.name.toLowerCase() === botId.toLowerCase()
  );

  if (!bot) {
    return <NotFound />;
  }

  const getThemeStyles = (theme) => {
    switch (theme) {
      case 'purple':
        return {
          badge: 'bg-purple-950/60 text-fuchsia-300 border-purple-500/40',
          accentText: 'text-fuchsia-400',
          borderAccent: 'border-l-fuchsia-500',
          glow: 'bg-purple-600/10'
        };
      case 'emerald':
        return {
          badge: 'bg-purple-950/60 text-purple-200 border-purple-500/40',
          accentText: 'text-purple-300',
          borderAccent: 'border-l-purple-500',
          glow: 'bg-purple-600/10'
        };
      case 'indigo':
      default:
        return {
          badge: 'bg-pink-950/60 text-pink-300 border-pink-500/40',
          accentText: 'text-pink-400',
          borderAccent: 'border-l-pink-500',
          glow: 'bg-pink-600/10'
        };
    }
  };

  const styles = getThemeStyles(bot.theme);

  return (
    <div className="animate-fade-in-down max-w-4xl mx-auto space-y-8">
      <SEO
        title={`${bot.name} — Discord Bot`}
        description={bot.fullDescription || bot.description}
        path={`/projects/discordbots/${bot.slug || botId}`}
        keywords={[bot.name, 'Discord Bot', 'Koneko', 'NotKoneko', 'Discord.js', ...(bot.features || [])]}
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            getSoftwareAppSchema({
              name: bot.name,
              description: bot.fullDescription || bot.description,
              applicationCategory: 'Discord Bot / Communication Application',
              operatingSystem: 'Discord / Cloud Service',
              url: `/projects/discordbots/${bot.slug || botId}`,
              codeRepository: bot.repoUrl,
              featureList: bot.features || []
            }),
            getBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Projects', url: '/projects' },
              { name: 'Discord Bots', url: '/projects/discordbots' },
              { name: bot.name, url: `/projects/discordbots/${bot.slug || botId}` }
            ])
          ]
        }}
      />
      {/* Back Button */}
      <Link 
        to="/projects/discordbots" 
        className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 transition-colors group"
      >
        <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Discord Bots
      </Link>

      {/* Hero Banner */}
      <div className={`bg-[#090714] p-5 sm:p-8 border border-purple-500/20 border-l-4 ${styles.borderAccent} rounded-2xl sm:rounded-3xl shadow-2xl relative overflow-hidden`}>
        <div className={`absolute top-0 right-0 w-80 h-80 ${styles.glow} rounded-full blur-[140px] pointer-events-none`} />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 border-b border-purple-500/20 pb-6 relative z-10">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-3 sm:p-4 bg-[#5865F2]/15 border border-[#5865F2]/40 rounded-2xl text-[#5865F2] flex items-center justify-center shrink-0 shadow-lg shadow-[#5865F2]/20">
              <svg className="w-6 h-6 sm:w-8 sm:h-8 fill-[#5865F2]" viewBox="0 0 127.14 96.36">
                <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1,105.25,105.25,0,0,0,32.19-16.14c2.64-27.38-4.51-51.11-18.91-72.15ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,45.91,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,45.91,96.12,53,91.08,65.69,84.69,65.69Z"/>
              </svg>
            </div>
            <div>
              <span className={`text-xs font-mono px-2.5 py-1 border rounded-md ${styles.badge} mb-2 inline-block`}>
                {bot.tag}
              </span>
              <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">{bot.name}</h1>
            </div>
          </div>

          <span className="text-xs font-mono px-3 py-1.5 bg-[#0d0a1c] text-purple-300 rounded-full border border-purple-500/20 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
            Status: {bot.status}
          </span>
        </div>

        <p className="text-base sm:text-xl text-gray-200 leading-relaxed mb-6 font-serif italic relative z-10">
          — {bot.fullDescription || bot.description}
        </p>

        <div className="flex items-center gap-4 text-xs font-mono text-gray-400 pt-4 border-t border-purple-500/20 relative z-10">
          <span>Technology Stack:</span>
          <span className={`${styles.accentText} font-semibold bg-[#0d0a1c] px-3 py-1 rounded-md border border-purple-500/20`}>
            {bot.tech}
          </span>
        </div>
      </div>

      {/* Grid: Features & Command Terminal */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Features Checklist */}
        <div className="bg-[#090714] p-6 border border-purple-500/20 rounded-2xl shadow-xl flex flex-col justify-between">
          <div>
            <h3 className="text-lg font-serif font-bold text-white mb-4 flex items-center gap-2 border-b border-purple-500/20 pb-3">
              <span className={styles.accentText}>⚡</span> Core Capabilities
            </h3>
            <ul className="space-y-3 font-mono text-xs">
              {bot.features?.map((feature, idx) => (
                <li key={idx} className="flex items-start gap-3 text-gray-300">
                  <span className={`${styles.accentText} font-bold mt-0.5`}>✓</span>
                  <span className="leading-snug">{feature}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Command Terminal Interface */}
        <div className="bg-[#090714] p-6 border border-purple-500/20 rounded-2xl shadow-xl flex flex-col justify-between font-mono">
          <div>
            <h3 className="text-lg font-serif font-bold text-white mb-4 flex items-center gap-2 border-b border-purple-500/20 pb-3">
              <span className="text-fuchsia-400">&gt;_</span> Command Interface
            </h3>
            <div className="space-y-2">
              {bot.commands?.map((cmd, idx) => (
                <div key={idx} className="bg-[#0d0a1c] p-2.5 rounded-lg border border-purple-500/20 flex items-center justify-between text-xs hover:border-purple-500/40 transition-colors">
                  <span className="text-fuchsia-400">{cmd}</span>
                  <span className="text-[10px] text-purple-300 bg-purple-950/40 px-2 py-0.5 rounded border border-purple-500/20">Slash Command</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* System Architecture Section */}
      {bot.architecture && (
        <div className="bg-[#090714] p-8 border border-purple-500/20 rounded-2xl shadow-xl space-y-4">
          <h3 className="text-xl font-serif font-bold text-white flex items-center gap-3 border-b border-purple-500/20 pb-4">
            <span className={styles.accentText}>⚙️</span> System Architecture &amp; Engine Specs
          </h3>
          <p className="text-gray-300 font-mono text-sm leading-relaxed bg-[#0d0a1c] p-5 rounded-xl border border-purple-500/20">
            {bot.architecture}
          </p>
        </div>
      )}
    </div>
  );
};

export default DiscordBotDetail;
